// client/src/utils/academicYearContext.js
import { fetchJson } from './fetchJson';

const KEY = 'currentAcademicYearId';

function getEffectiveKey() {
  const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId');
  return schoolId ? `${schoolId}:${KEY}` : KEY;
}

export function getCurrentAcademicYearId() {
  const k = getEffectiveKey();
  let id = localStorage.getItem(k);
  if (!id) id = localStorage.getItem(KEY); // legacy fallback
  let n = Number(id);
  if (!Number.isFinite(n) && typeof id === 'string') {
    try {
      const trimmed = id.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const obj = JSON.parse(trimmed);
        const cand = obj?.id ?? obj?.value ?? obj?.sessionId ?? obj?.academicYearId ?? obj?.ayId ?? obj?.yearId;
        n = Number(cand);
      }
    } catch (_) {}
  }
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Store the academic year the app is scoped to.
 *
 * WRITES THE SAME FIVE KEYS THE PHONE WRITES — `setAcademicYear` in
 * @ameca/core/scope. This wrote only the school-namespaced id, and the
 * asymmetry mattered:
 *
 *   fetchJson resolves the header as `namespaced || global`, so the two keys
 *   are not alternatives, they are a fallback chain. bootstrapAcademicYearFromServer
 *   writes BOTH, but a user PICKING a year wrote only the namespaced one — so
 *   after a switch the global key still held the school's current year. Any
 *   reader that reaches the fallback (no school id resolved yet, a legacy key,
 *   a fresh tab mid-bootstrap) then answered with a different year than the
 *   switcher was showing. The phone's own comment is explicit about why it
 *   writes both: "otherwise a stale global value could win".
 *
 *   The name and the dates were never stored at all, so nothing on the web
 *   could name the selected year or build its month range without refetching
 *   the catalogue to translate an id — which is exactly what the phone stores
 *   them to avoid.
 *
 * @param {number|object} year  an id, or the year row (id, name, startsAt, endsAt)
 */
export function setCurrentAcademicYearId(year) {
  const n = Number(year?.id ?? year);
  if (!Number.isFinite(n) || n <= 0) return;
  const k = getEffectiveKey();
  localStorage.setItem(k, String(n));
  // The un-namespaced key is the fallback half of the chain above, so it moves
  // with the namespaced one rather than being left behind holding an older year.
  try { localStorage.setItem(KEY, String(n)); } catch (_) {}

  const meta = typeof year === 'object' && year ? year : null;
  if (meta) {
    try {
      if (meta.name) localStorage.setItem('currentAcademicYearName', meta.name);
      const startsAt = meta.startsAt || meta.startDate;
      const endsAt = meta.endsAt || meta.endDate;
      if (startsAt) localStorage.setItem('currentAcademicYearStartsAt', startsAt);
      if (endsAt) localStorage.setItem('currentAcademicYearEndsAt', endsAt);
    } catch (_) {}
  }

  try {
    window.dispatchEvent(new CustomEvent('app:ay:changed', { detail: { id: n } }));
  } catch (_) {}
}

/**
 * Is the year being VIEWED a finished one?
 *
 * Mirrors `isPastAcademicYear` in @ameca/core/scope, which the phone uses, and
 * reads the same two keys — the two clients must agree about which sessions are
 * closed or they will disable different things.
 *
 * FALSE WHEN UNKNOWN, deliberately. If the school's current year has not been
 * resolved yet there is nothing to compare against, and blocking every write on
 * a value that has not loaded would break the app on a slow connection. The
 * server enforces this regardless — `enforceCurrentYear` refuses the write —
 * so a wrong answer here costs a refused request, never a silent edit.
 */
export function isPastAcademicYear() {
  const viewing = Number(getCurrentAcademicYearId());
  let current = null;
  try { current = Number(localStorage.getItem('schoolCurrentAcademicYearId')); } catch (_) { /* no storage */ }
  if (!Number.isFinite(viewing) || !Number.isFinite(current) || !current) return false;
  return viewing !== current;
}

export function onAcademicYearChanged(handler) {
  const h = (e) => handler?.(e?.detail?.id ?? getCurrentAcademicYearId());
  window.addEventListener('app:ay:changed', h);
  return () => window.removeEventListener('app:ay:changed', h);
}

/**
 * The academic year the SCHOOL is currently in.
 *
 * This endpoint is the system's own answer: it applies the canonical rule —
 * the year whose [startsAt, endsAt] contains today, else the next one to start
 * (server/utils/currentAcademicYear.js) — and persists the result to
 * School.currentGlobalAcademicYearId. It is readable by any authenticated
 * user, which is why the School Settings page has always shown the right year
 * while the rest of the app did not.
 *
 * The year therefore ADVANCES BY ITSELF when a session's window elapses; it is
 * not a flag anyone has to remember to move.
 *
 * @returns {Promise<{id:number,name:string}|null>}
 */
export const fetchSchoolCurrentAcademicYear = async () => {
  try {
    const res = await fetchJson('/api/school-academic-years/current');
    const id = Number(res?.currentGlobalAcademicYearId ?? res?.currentYear?.id);
    if (Number.isFinite(id) && id > 0) {
      const y = res?.currentYear || {};
      // Dates come back too, so the session's month range can be stored with
      // the id — the phone keeps all four together for the same reason.
      return {
        id,
        name: y.name || '',
        startsAt: y.startsAt || y.startDate || null,
        endsAt: y.endsAt || y.endDate || null,
      };
    }
  } catch (_) { /* fall through to the global catalogue */ }
  return null;
};

/**
 * Resolve and store the academic year the whole app is scoped to.
 *
 * Previously this read /api/global-academic-years and took
 * `years.find(y => y.isCurrent) || years[0]`. Against real data that is wrong
 * in a way that is invisible until you look:
 *
 *   - No global year is necessarily flagged isCurrent, so it falls to years[0],
 *     which is simply the lowest id — the OLDEST session.
 *   - The chosen id is then sent as X-Academic-Year-Id, and the server treats
 *     that header as the highest-priority source (academicYearContext.js). So
 *     the client OVERRIDES the school's own selection on every request.
 *   - The value is cached in localStorage, so once wrong it stays wrong across
 *     reloads and the switcher never catches up.
 *
 * That is why teacher and parent pages kept showing the previous year's data
 * while School Settings showed the correct one: only Settings asked the school.
 *
 * Writes are affected too, not just reads — enforceCurrentYear rejects any
 * mutation whose year does not match the school's current one, with "Past
 * academic years are read-only".
 */
export const bootstrapAcademicYearFromServer = async () => {
  const school = await fetchSchoolCurrentAcademicYear();
  if (school) {
    // Overwrite unconditionally. A stale stored year is precisely the fault
    // being fixed, so preserving it would defeat the purpose; an admin who
    // wants to inspect a past session picks it again from the switcher.
    //
    // THIS is the one place allowed to reset a stored choice, and it is the
    // counterpart of the phone's bootstrapScope: it runs at load and after
    // sign-in, not on every component mount. The switcher must not repeat it,
    // or the two fight over the same key and the user's pick loses.
    try { setCurrentAcademicYearId(school); } catch (_) {}
    // THE SCHOOL'S CURRENT YEAR, KEPT SEPARATELY from the one being viewed.
    //
    // Without this the web could not tell the two apart: the switcher writes
    // the chosen year over `currentAcademicYearId`, the same key the school's
    // own year was stored in, so after picking a finished session there was
    // nothing left to compare against. That is why nothing on the web ever said
    // "this year is read-only" — it had no way to know. The phone has stored
    // both since it was written, under this same key.
    try { localStorage.setItem('schoolCurrentAcademicYearId', String(school.id)); } catch (_) {}
    return school.id;
  }

  try {
    const years = await fetchJson('/api/global-academic-years', { skipAuth: true });
    if (years?.length > 0) {
      const currentYear = years.find(y => y.isCurrent) || years[0];
      try { setCurrentAcademicYearId(currentYear.id); } catch (_) {}
      try { localStorage.setItem('currentAcademicYearId', String(currentYear.id)); } catch (_) {}
      return currentYear.id;
    }
  } catch (error) {
    console.error('Academic year bootstrap failed, using localStorage fallback', error);
    const stored = localStorage.getItem('currentAcademicYearId');
    if (stored) {
      try {
        const n = Number(stored);
        if (Number.isFinite(n) && n > 0) return n;
        return JSON.parse(stored).id;
      } catch (e) {
        console.warn('Failed to parse stored academic year', e);
      }
    }
  }
  return null;
};

// Sync the locally selected academic year back to the server (best-effort, non-blocking)
export async function syncAcademicYearWithServer() {
  try {
    const id = getCurrentAcademicYearId();
    if (!id) return;
    // THE ENDPOINT THAT EXISTS. This tried PUT /api/user-settings/academic-year
    // and then fell back to POST /api/user-settings — neither of which the
    // server has ever served. routes/userSettings.js declares /settings,
    // /profile, /profile/photo and /profile/password, and nothing else.
    //
    // Both attempts 404'd and both were swallowed by their own catch, so
    // choosing an academic year appeared to work and was never persisted: it
    // survived until the tab was closed and then reverted on the next sign-in.
    // A "fallback" to a second endpoint that also does not exist is what made it
    // look deliberate.
    try {
      await fetchJson('/api/user-settings/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ academicYearId: id })
      });
    } catch (_) { /* the selection still holds locally for this session */ }
  } catch (_) {}
}